import { useState, useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { TabList, Tabs, TabPanel, StyledTab } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import {
    CATEGORIES_COMPARISON_COLORS,
    useCategoriesComparison,
} from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { BasicDurations } from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useHighLevelMetricsComparisonData } from "../hooks/useHighLevelMetricsComparisonData";
import type { MetricKey } from "../types";

import { METRIC_CONFIGURATION, METRIC_KEYS, isMetricSupportedForGranularity } from "./config";
import { HighLevelMetricsComparisonChart } from "./HighLevelMetricsComparisonChart";
import { HighLevelMetricsComparisonWidgetSkeleton } from "./HighLevelMetricsComparisonWidgetSkeleton";

const StyledTabPanel = styled(TabPanel)`
    padding: 24px;
    background: ${colorsPalettes.carbon[0]};
`;

export const HighLevelMetricsComparison = () => {
    const { translate } = useTranslate();
    const subtitleFilters = useSubtitleFilters("date");
    const {
        params: { duration, selectedMetric: urlSelectedMetric },
    } = useCurrentStateAndParams();
    const { categories, hasSelectedCategoryWithSegments } = useCategoriesComparison();
    const { categoriesData, isPeriodOverPeriod, isLoading, isError } = useHighLevelMetricsComparisonData(categories);

    const is28d = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const isUniqueVisitorsUnavailable = is28d || hasSelectedCategoryWithSegments;

    const availableMetricKeys = useMemo(() => {
        if (isUniqueVisitorsUnavailable) {
            return METRIC_KEYS.filter((key) => key !== "unique-visitors");
        }
        return METRIC_KEYS;
    }, [isUniqueVisitorsUnavailable]);

    const initialMetric = useMemo(() => {
        if (urlSelectedMetric && availableMetricKeys.includes(urlSelectedMetric as MetricKey)) {
            return urlSelectedMetric as MetricKey;
        }
        return availableMetricKeys[0];
    }, [urlSelectedMetric, availableMetricKeys]);

    const initialTabIndex = availableMetricKeys.indexOf(initialMetric);

    const [currentTabIndex, setCurrentTabIndex] = useState(initialTabIndex);
    const [selectedMetric, setSelectedMetric] = useState<MetricKey>(initialMetric);
    const [timeGranularity, setTimeGranularity] = useState<granularityItem>(
        is28d ? toggleItems.Daily : toggleItems.Monthly,
    );

    const [legendItems, setLegendItems] = useState<ILegendItem[]>([]);

    useEffect(() => {
        if (categoriesData.length > 0) {
            setLegendItems((prevItems) =>
                categoriesData.map(({ category }) => {
                    const categoryComparison = categories.find(
                        (cat) => (cat.category.text || cat.category.name) === category.name,
                    );
                    const color = categoryComparison?.color ?? CATEGORIES_COMPARISON_COLORS[0];

                    return {
                        id: category.id,
                        name: category.name,
                        color,
                        visible: prevItems.find((item) => item.id === category.id)?.visible ?? true,
                    };
                }),
            );
        }
    }, [categoriesData, categories]);

    useEffect(() => {
        if (
            selectedMetric === "unique-visitors" &&
            !isMetricSupportedForGranularity(selectedMetric, timeGranularity.value)
        ) {
            setTimeGranularity(toggleItems.Monthly);
        }
    }, [selectedMetric, timeGranularity.value]);

    // Force Monthly granularity for period-over-period mode
    useEffect(() => {
        if (isPeriodOverPeriod && timeGranularity.value !== toggleItems.Monthly.value) {
            setTimeGranularity(toggleItems.Monthly);
        }
    }, [isPeriodOverPeriod, timeGranularity.value]);

    const toggleLegendItem = (legendItem: ILegendItem) => {
        setLegendItems((prevItems) =>
            prevItems.map((item) => (item.id === legendItem.id ? { ...item, visible: !item.visible } : item)),
        );
    };

    const handleGranularityChange = (selectedIndex: number) => {
        const granularityOptions = [toggleItems.Daily, toggleItems.Weekly, toggleItems.Monthly];
        const newGranularity = granularityOptions[selectedIndex];

        if (isMetricSupportedForGranularity(selectedMetric, newGranularity.value)) {
            setTimeGranularity(newGranularity);
        }
    };

    const handleSelectTab = (index: number) => {
        setCurrentTabIndex(index);
        const newMetric = availableMetricKeys[index];
        setSelectedMetric(newMetric);

        void proNavigator.updateParams({ selectedMetric: newMetric });

        if (newMetric === "unique-visitors" && !isMetricSupportedForGranularity(newMetric, timeGranularity.value)) {
            setTimeGranularity(toggleItems.Monthly);
        }
    };

    useEffect(() => {
        if (isUniqueVisitorsUnavailable && selectedMetric === "unique-visitors") {
            const firstAvailableMetric = availableMetricKeys[0];
            setSelectedMetric(firstAvailableMetric);
            setCurrentTabIndex(0);
            void proNavigator.updateParams({ selectedMetric: firstAvailableMetric });
        } else {
            const newIndex = availableMetricKeys.indexOf(selectedMetric);
            if (newIndex !== -1 && newIndex !== currentTabIndex) {
                setCurrentTabIndex(newIndex);
            }
        }
    }, [isUniqueVisitorsUnavailable, selectedMetric, availableMetricKeys, currentTabIndex]);

    return (
        <WidgetContainer
            withBorder
            title={translate("analysis.traffic.engagement.title")}
            subtitleFilters={subtitleFilters}
            isLoading={isLoading}
            noData={isError}
            minHeight={440}
            automationId="industry-analysis-overview-comparison-traffic-and-engagement"
            LoaderComponent={<HighLevelMetricsComparisonWidgetSkeleton />}
        >
            <Tabs
                size="large"
                selectedIndex={currentTabIndex}
                onSelect={handleSelectTab}
            >
                <TabList>
                    {availableMetricKeys.map((key) => (
                        <StyledTab key={key}>
                            <Flex
                                gap="8px"
                                paddingY="4px"
                            >
                                <SWReactIcons
                                    iconName={METRIC_CONFIGURATION[key].iconName}
                                    size="xs"
                                />
                                {METRIC_CONFIGURATION[key].metricName}
                            </Flex>
                        </StyledTab>
                    ))}
                </TabList>
                {availableMetricKeys.map((key) => (
                    <StyledTabPanel key={key}>
                        <HighLevelMetricsComparisonChart
                            categoriesData={categoriesData}
                            isPeriodOverPeriod={isPeriodOverPeriod}
                            selectedMetric={selectedMetric}
                            timeGranularity={timeGranularity}
                            legendItems={legendItems}
                            toggleLegendItem={toggleLegendItem}
                            onGranularityChange={handleGranularityChange}
                        />
                    </StyledTabPanel>
                ))}
            </Tabs>
        </WidgetContainer>
    );
};
